import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardBody, CardHeader, Label, Badge, Input, Button } from "reactstrap";
import Loader from "../../Components/Common/Loader";
import ForeCastPreviewCardHeader from "../../Components/Common/ForeCastPreviewCardHeader";
import Chart from "react-apexcharts";
import { DISCOUNT } from "../../helpers/data_helper";
import { postDataAPI } from "../../helpers/phpbackend_helper";

const DiscountTrends = ({ mealPeriod, leftFilter, discountCodes, serveList, allDiscountList, changeDiscountCodes }) => {
    const [doccupancy, setDoccupancy] = useState('Seat');
    const [selectedDiscounts, setSelectedDiscounts] = useState([]);
    const [updateDiscounts, setUpdateDiscounts] = useState([]);
    const changeOccupancy = () => {
        setDoccupancy(doccupancy === "Seat" ? "Table" : "Seat");
    }
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hours, setHours] = useState([]);
    const didMount = useRef(false);

    // useEffect(() => {
    //     setUpdateDiscounts([])
    //     if (!didMount.current) {
    //         didMount.current = true;
    //     } else {
    //         const allValues = allDiscountList.map(d => d.value);
    //         setSelectedDiscounts(allValues);
    //         setUpdateDiscounts(allValues);
    //         // setSelectedDiscounts([]);
    //         // setUpdateDiscounts([]);
    //     }
    // }, [mealPeriod, leftFilter, serveList, doccupancy, allDiscountList]);

    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            return;
        }
        const allValues = allDiscountList.map(d => d.value);
        setSelectedDiscounts(allValues);
        setUpdateDiscounts(allValues); 
    }, [mealPeriod, leftFilter, serveList, doccupancy, allDiscountList]);


    const fetchData = async () => {
        setLoading(true);
        setSeries([]);
        const percentages = [91, 81, 71, 61, 51, 41, 31, 21, 11, 0];
        try {
            let allHoursSet = new Set();
            const allResults = await Promise.all(
                percentages.map(async (fromPercentage) => {
                    const response = await postDataAPI({
                        serviceName: DISCOUNT.serviceName,
                        op: DISCOUNT.op_discount_occupancy_heatmap,
                        leftFilter,
                        mealPeriod,
                        server_id: serveList,
                        discount_id: updateDiscounts,
                        occupancy: doccupancy === "Table" ? "table_occ" : "seat_occ",
                        fromPercentage,
                    });

                    if (response.status === "ok") {
                        const rawData = response.data?.data || [];
                        const hours = response.data?.hours || [];
                        hours.forEach(hour => allHoursSet.add(hour));

                        return rawData.map(series => {
                            const hourMap = {};
                            (Array.isArray(series.data) ? series.data : []).forEach(item => {
                                if (item?.x && item?.y) {
                                    let total = 0;
                                    let breakdown = {};
                                    if (typeof item.y === 'object') {
                                        breakdown = item.y;
                                        total = Object.values(item.y).reduce((sum, val) => sum + Number(val), 0);
                                    } else {
                                        total = Number(item.y);
                                    }
                                    hourMap[item.x] = { y: total, meta: breakdown };
                                }
                            });

                            return {
                                name: series.name,
                                fromPercentage,
                                hourMap, // Temp map we'll use later
                            };
                        });
                    } else {
                        console.error(`Error for ${fromPercentage}%:`, response.message);
                        return [];
                    }
                })
            );

            // Get sorted unique hours (morning to night)
            const uniqueHours = Array.from(allHoursSet).sort((a, b) => {
                const parseTime = (t) => {
                    const match = t.match(/^(\d{1,2})(?::(\d{2}))?(AM|PM)$/i);
                    if (!match) return 0;
                    let hour = parseInt(match[1], 10);
                    const minutes = parseInt(match[2] || "0", 10);
                    const meridiem = match[3].toUpperCase();
                    if (meridiem === "PM" && hour !== 12) hour += 12;
                    if (meridiem === "AM" && hour === 12) hour = 0;
                    return hour * 60 + minutes;
                };
                return parseTime(a) - parseTime(b);
            });

            setHours(uniqueHours);

            // Final format: convert hourMap to full data array for all hours
            const finalSeries = allResults.flat().map(series => ({
                name: series.name,
                data: uniqueHours.map(hour => {
                    return {
                        x: hour,
                        y: series.hourMap?.[hour]?.y ?? 0,
                        meta: series.hourMap?.[hour]?.meta ?? {}
                    };
                })
            }));

            setSeries(finalSeries);
        } catch (err) {
            console.error("API Error:", err);
        } finally {
            setLoading(false);
        }
    };




    // useEffect(() => {
    //     fetchData();
    // }, [doccupancy, leftFilter, mealPeriod, serveList]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchData();
        }, 500); // debounce
        return () => clearTimeout(timeout);
    }, [updateDiscounts]);

    // const handleDiscountClick = (badge) => {
    //     const updatedBadges = selectedDiscounts.includes(badge)
    //         ? selectedDiscounts.filter((value) => value !== badge)
    //         : [...selectedDiscounts, badge];

    //     setSelectedDiscounts(updatedBadges);

    //     // const updatedDiscounts = updatedBadges.map((value) => ({
    //     //     value,
    //     //     label: value 
    //     // }));

    //     // changeDiscountCodes(updatedDiscounts);
    // };

    const handleDiscountClick = (badge) => {
        const updatedBadges = selectedDiscounts.includes(badge)
            ? selectedDiscounts.filter((value) => value !== badge)
            : [...selectedDiscounts, badge];

        setSelectedDiscounts(updatedBadges);
    };

    const handleReload = () => {
        setUpdateDiscounts(selectedDiscounts);
    };

    const generateDynamicRanges = (data) => {
        const allValues = data.flatMap(row => row.data.map(point => point.y));
        const maxValue = Math.max(...allValues);
        const step = Math.ceil(maxValue / 5); // Divide range by 5 instead of 6

        return [
            { from: 0, to: 0, name: 'Extremely Low', color: '#f8f9fa' },
            { from: 1, to: step, name: 'Very Low', color: '#d3f2c2' },
            { from: step + 1, to: 2 * step, name: 'Low', color: '#91e05c' },
            { from: 2 * step + 1, to: 3 * step, name: 'Medium', color: '#f9d776' },
            { from: 3 * step + 1, to: 4 * step, name: 'High', color: '#f8ac5c' },
            { from: 4 * step + 1, to: maxValue, name: 'Very High', color: '#f17171' }
        ];
    };



    const dynamicColorRanges = generateDynamicRanges(series);

    const xAxisOffsetX = useMemo(() => {
        if (window.innerWidth > 2000) return -850;
        if (window.innerWidth > 1800) return -740;
        if (window.innerWidth > 1700) return -680;
        if (window.innerWidth > 1500) return -590;
        if (window.innerWidth > 1500) return -590;
        if (window.innerWidth > 1350) return -520;
        if (window.innerWidth > 1200) return -450;
        if (window.innerWidth > 1000) return -370;
        if (window.innerWidth > 768) return -320;
        return -100;
    }, []);

    const handleSelectAllToggle = () => {
        if (selectedDiscounts.length === allDiscountList.length) {
            setSelectedDiscounts([]);
        } else {
            const allValues = allDiscountList.map(d => d.value);
            setSelectedDiscounts(allValues);
        }
    };

    return (
        <Card>
            {series.length > 0 ?
                <>
                    <CardHeader className="align-items-center d-flex">
                        <h4 className="card-title mb-0 flex-grow-1">
                            Discount Heatmap by Hour and Occupancy
                        </h4>
                        <div className="flex-shrink-0">
                            <div className="form-check form-switch form-switch-right form-switch-md d-flex align-items-center gap-2">
                                <Label className="form-label text-muted mb-0" style={{ marginRight: "5px" }}>Table</Label>
                                <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={doccupancy === "Seat"}
                                    onChange={changeOccupancy}
                                />
                                <Label className="form-label text-muted mb-0 ms-2">Seat</Label>
                                <Button color="primary" className="btn-label rounded-pill btn-sm" onClick={handleReload}> <i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i> Reload </Button>
                            </div>
                        </div>

                    </CardHeader>

                    <CardBody>
                        {(loading || !series.length) && <Loader />}
                        <div id="DiscountTrends"
                            className={
                                loading
                                    ? "opacity-0 table-responsive table-card"
                                    : "table-responsive table-card"
                            }
                            style={{ maxHeight: '780px', overflow: 'hidden', overflowX: 'scroll' }}
                        >

                            <div className="d-flex justify-content-between align-items-center ms-3 mt-1 mr-5">
                                <Label className="ms-1 mt-1 fw-normal">Number of discounts given across time and occupancy levels</Label>
                                <Badge
                                    color="primary"
                                    onClick={handleSelectAllToggle}
                                    style={{
                                        minWidth: "120px",
                                        textAlign: "center",
                                        cursor: "pointer",
                                        backgroundColor: "#364574",
                                        color: "#fff",
                                        border: "1px solid #4A5882",
                                        marginRight: "30px"
                                    }}
                                >
                                    {selectedDiscounts.length === allDiscountList.length ? "Deselect All Discount" : "Select All Discount"}
                                </Badge>
                            </div>
                            <div className="d-flex flex-wrap gap-2 ms-3">
                                {allDiscountList.map((badge) => (
                                    <Badge
                                        key={badge.value}
                                        color={selectedDiscounts.includes(badge.value) ? "danger" : "light"}
                                        onClick={() => handleDiscountClick(badge.value)}
                                        style={{
                                            cursor: "pointer",
                                            backgroundColor: selectedDiscounts.includes(badge.value) ? "" : "#d3d3d3", // light gray
                                            color: selectedDiscounts.includes(badge.value) ? "" : "#000", // black text
                                            border: selectedDiscounts.includes(badge.value) ? "1px solid #F06548" : "1px solid #ccc" // optional, for subtle outline
                                        }}
                                    >
                                        {badge.label}
                                    </Badge>
                                ))}

                            </div>
                            <Chart
                                options={{
                                    chart: { type: "heatmap" },
                                    dataLabels: {
                                        enabled: true,
                                        style: { colors: ['#000'] }
                                    },
                                    xaxis: {
                                        categories: hours, position: "top",
                                        labels: { show: true, style: { fontSize: '12px' } },
                                        title: {
                                            text: "Occupancy",
                                            offsetX: xAxisOffsetX,
                                            offsetY: 18,
                                            style: {
                                                fontSize: "10px"
                                            }
                                        }
                                    },
                                    yaxis: {
                                        labels: { show: true, style: { fontSize: '12px' } },
                                    },
                                    plotOptions: {
                                        heatmap: {
                                            radius: 0,
                                            enableShades: false,
                                            colorScale: {
                                                ranges: dynamicColorRanges,
                                            },


                                        }
                                    },
                                    grid: { padding: { right: 0, left: 30 } },
                                    legend: { show: false },
                                    colors: [],
                                    tooltip: {
                                        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                                            const data = w.config.series[seriesIndex].data[dataPointIndex];
                                            const breakdown = data.meta || {};

                                            const title = `<div style="font-weight: bold; margin-bottom: 6px;">
                                            ${w.config.series[seriesIndex].name}
                                        </div>`;

                                            const rows = Object.entries(breakdown).map(([key, value]) =>
                                                `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
                                    <span style="color: #444;">${key}: </span> 
                                    <span style="color: #000;"> &nbsp; ${value}</span>
                                </div>`
                                            ).join('');

                                            const container = `
                                <div style="padding: 10px; max-width: 250px; font-size: 13px; font-family: sans-serif;">
                                    ${title}
                                    ${rows || '<div style="color: #999;">No data</div>'}
                                </div>
                            `;

                                            return container;
                                        }
                                    }


                                }}
                                series={series}
                                type="heatmap"
                                height={500}
                            />

                        </div>
                    </CardBody>
                </>
                :
                <>
                    <CardHeader className="align-items-center d-flex">
                        <h4 className="card-title mb-0 flex-grow-1">
                            Discount Heatmap by Hour and Occupancy
                        </h4>
                        <div className="flex-shrink-0">
                            <div className="form-check form-switch form-switch-right form-switch-md d-flex align-items-center gap-2">
                                <Label className="form-label text-muted mb-0" style={{ marginRight: "5px" }}>Table</Label>
                                <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={doccupancy === "Seat"}
                                    onChange={changeOccupancy}
                                />
                                <Label className="form-label text-muted mb-0 ms-2">Seat</Label>
                                <Button color="primary" className="btn-label rounded-pill btn-sm" onClick={handleReload}> <i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i> Reload </Button>
                            </div>
                        </div>

                    </CardHeader>
                    <CardBody>
                        {(loading || !series.length) && <Loader />}
                    </CardBody>
                </>
            }
        </Card>
    );
};

export default DiscountTrends;
